<template>
    <branded-split-screen
        heading="Start your free trial"
        description="Create your account and start shipping features safely in minutes. No credit card required."
        :highlights="highlights"
    >
        <h2 class="text-2xl font-semibold text-taupe-900 dark:text-taupe-50">
            Create your account
        </h2>
        <p class="mt-1 text-sm text-taupe-500 dark:text-taupe-400">
            Or
            <router-link
                :to="{ name: 'LoginScreen' }"
                class="font-medium text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                >login to an existing account!</router-link
            >
        </p>

        <div v-if="success" class="mt-8 space-y-4">
            <h3 class="text-xl font-medium text-taupe-900 dark:text-taupe-50">
                Congratulations!
            </h3>
            <p class="text-taupe-600 dark:text-taupe-300">
                We've created your account. Check your inbox for an email to
                confirm your address and log in.
            </p>
        </div>
        <form
            v-if="!success"
            @submit.prevent="submit"
            @keydown.enter="submitFormOnEnter"
            class="mt-8 space-y-6"
        >
            <zen-message-block v-if="message" type="error">
                {{ message }}
            </zen-message-block>

            <div>
                <label
                    for="company_name"
                    class="block font-medium text-taupe-800 dark:text-taupe-100"
                    >Company name</label
                >
                <div class="mt-1">
                    <input
                        id="company_name"
                        name="company_name"
                        required
                        v-model="form.company_name"
                        class="appearance-none block w-full px-3 py-2 border border-taupe-300 rounded-md shadow-xs placeholder-taupe-400 dark:placeholder-taupe-500 focus:outline-hidden focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800 dark:text-taupe-50"
                    />
                </div>
            </div>

            <div>
                <label
                    for="first_name"
                    class="block font-medium text-taupe-800 dark:text-taupe-100"
                >
                    Your first name
                </label>
                <div class="mt-1">
                    <input
                        id="first_name"
                        name="first_name"
                        required
                        v-model="form.first_name"
                        class="appearance-none block w-full px-3 py-2 border border-taupe-300 rounded-md shadow-xs placeholder-taupe-400 dark:placeholder-taupe-500 focus:outline-hidden focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800 dark:text-taupe-50"
                    />
                </div>
            </div>

            <div>
                <label
                    for="last_name"
                    class="block font-medium text-taupe-800 dark:text-taupe-100"
                >
                    Your last name
                </label>
                <div class="mt-1">
                    <input
                        id="last_name"
                        name="last_name"
                        required
                        v-model="form.last_name"
                        class="appearance-none block w-full px-3 py-2 border border-taupe-300 rounded-md shadow-xs placeholder-taupe-400 dark:placeholder-taupe-500 focus:outline-hidden focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800 dark:text-taupe-50"
                    />
                </div>
            </div>

            <div>
                <label
                    for="email"
                    class="block font-medium text-taupe-800 dark:text-taupe-100"
                >
                    Email address
                </label>
                <div class="mt-1">
                    <input
                        id="email"
                        name="email"
                        type="email"
                        autocomplete="email"
                        required
                        v-model="form.email"
                        class="appearance-none block w-full px-3 py-2 border border-taupe-300 rounded-md shadow-xs placeholder-taupe-400 dark:placeholder-taupe-500 focus:outline-hidden focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800 dark:text-taupe-50"
                    />
                </div>
            </div>

            <div class="space-y-3">
                <label class="flex items-start gap-3 cursor-pointer">
                    <input
                        id="terms"
                        name="terms"
                        type="checkbox"
                        required
                        v-model="form.terms"
                        class="mt-1 h-4 w-4 rounded border-taupe-300 text-sky-500 focus:ring-sky-500 shrink-0 dark:border-taupe-600 dark:bg-taupe-800"
                    />
                    <span class="text-sm text-taupe-700 dark:text-taupe-200">
                        I agree to the
                        <a
                            href="https://zenmanage.com/legal/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300 font-medium"
                            >Terms of Service</a
                        >
                        and
                        <a
                            href="https://zenmanage.com/legal/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300 font-medium"
                            >Privacy Policy</a
                        >.
                    </span>
                </label>
            </div>

            <div>
                <zen-button
                    type="submit"
                    variant="primary"
                    size="large"
                    full-width
                    :processing="loading"
                >
                    Create Account
                </zen-button>
            </div>
        </form>

        <social-login-buttons v-if="!success" />
    </branded-split-screen>
</template>

<script>
import BrandedSplitScreen from '@/screens/_components/BrandedSplitScreen.vue'
import SocialLoginButtons from '@/screens/_components/social/SocialLoginButtons.vue'
import AuthRegisterService from '@/services/auth.register.service'
import { BRAND_HIGHLIGHTS } from '@/utils/brand-highlights'
import { submitFormOnEnter } from '@/utils/form-submit'

export default {
    components: {
        BrandedSplitScreen,
        SocialLoginButtons,
    },
    data() {
        return {
            form: {
                company_name: null,
                first_name: '',
                last_name: '',
                email: '',
                terms: false,
            },
            loading: false,
            success: false,
            message: '',
            highlights: BRAND_HIGHLIGHTS,
        }
    },
    methods: {
        submitFormOnEnter,
        submit() {
            this.loading = true
            AuthRegisterService.create({
                company_name: this.form.company_name,
                first_name: this.form.first_name,
                last_name: this.form.last_name,
                email: this.form.email,
            }).then(
                () => {
                    this.success = true
                    this.loading = false
                },
                (error) => {
                    this.loading = false
                    this.message = error
                }
            )
        },
    },
}
</script>
